import React from 'react';
import { Hourglass } from 'lucide-react';
import { resolveMediaUrl } from '../../lib/mediaUrl';

/**
 * The activity photo, with the three pieces of info that always sit on top of it:
 *
 *   top-left      charter company logo
 *   bottom-left   duration \u00b7 location
 *   bottom-right  FROM $price
 *
 * One component so the grid (2 or 3 across) and the list rows are identical, and so the
 * photo is always responsive: the grid uses an aspect ratio (it scales with the column
 * width) and the list stretches the photo to the row height. Every part is hidden when
 * that field is empty on the activity, nothing is invented.
 */
const Sparkle = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-10 w-10">
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.8 2.8M14.9 14.9l2.8 2.8M17.7 6.3l-2.8 2.8M9.1 14.9l-2.8 2.8" strokeLinecap="round" />
  </svg>
);

const ActivityCardImage = ({ activity: a, variant = 'grid' }) => {
  // Same fixed aspect ratio for grid AND list so every thumbnail is identically sized,
  // regardless of how much text/description a given row has (that column's height must
  // never dictate the photo's height).
  const shell = 'relative aspect-[4/3] w-full overflow-hidden bg-gray-100';

  return (
    <div className={shell} data-testid={`activity-image-${a.alias}`}>
      {a.images?.[0] ? (
        <img
          src={resolveMediaUrl(a.images[0])}
          alt={a.title}
          loading="lazy"
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-slate-400"><Sparkle /></div>
      )}

      {/* legibility wash so the overlaid info always reads */}
      <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />

      {/* charter company logo - TOP LEFT */}
      {a.seller_logo_url && (
        <div className="absolute left-2 top-2 rounded-lg bg-white/95 p-1 shadow-md" data-testid={`activity-card-seller-logo-${a.alias}`}>
          <img
            src={resolveMediaUrl(a.seller_logo_url)}
            alt={a.seller_name || 'Charter company'}
            title={a.seller_name || ''}
            className="h-8 w-auto max-w-[84px] object-contain sm:h-9 sm:max-w-[92px]"
          />
        </div>
      )}

      {/* duration sits in front of the location, as one line - BOTTOM LEFT */}
      {(a.duration || a.location) && (
        <div
          className="absolute bottom-2 left-2 flex max-w-[64%] items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm"
          data-testid={`activity-card-meta-${a.alias}`}
        >
          {a.duration && <Hourglass className="h-3 w-3 shrink-0" />}
          <span className="truncate">{[a.duration, a.location].filter(Boolean).join(' \u00b7 ')}</span>
        </div>
      )}

      {/* FROM price - BOTTOM RIGHT */}
      {a.price_from ? (
        <div className="absolute bottom-0 right-0 overflow-hidden rounded-tl-md text-center shadow-lg" data-testid={`activity-card-price-badge-${a.alias}`}>
          <div className="bg-white/95 px-3 py-1"><span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">From</span></div>
          <div className="bg-slate-100 px-3 py-1"><span className="text-sm font-black text-slate-900">${a.price_from}</span></div>
        </div>
      ) : null}
    </div>
  );
};

export default ActivityCardImage;
